import type { FC } from 'react';
import { defineMessage, defineMessages, useIntl } from 'react-intl';
import { useSearchParams } from 'next/navigation';

import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters/mergeQueryParameters';
import { rememberPageData } from '@packages/shared/src/hooks/useScrollRestoration/useScrollRestoration';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { CriteoProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/CriteoProductCardResultFragmentFragmentDoc';
import type { CriteoProductCardContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CriteoProductCardContentFragmentFragmentDoc';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { ProductCardV2Name } from '@packages/modules/src/ProductCardV2/ProductCardV2.Name';
import { ProductCardV2Brand } from '@packages/modules/src/ProductCardV2/ProductCardV2.Brand';
import { ProductCardV2Image } from '@packages/modules/src/ProductCardV2/ProductCardV2.Image';
import { ProductCardV2FlagsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.FlagsContainer';
import { ProductCardV2ExtendedDescription } from '@packages/modules/src/ProductCardV2/ProductCardV2.ExtendedDescription';
import { ProductCardV2Rating } from '@packages/modules/src/ProductCardV2/ProductCardV2.Rating';
import { ProductCardV2EnergyEfficiencyFlags } from '@packages/modules/src/ProductCardV2/ProductCardV2.EnergyEfficiencyFlags.client';
import { ProductCardV2EfficiencyFlagsContainer } from '@packages/modules/src/ProductCardV2/ProductCardV2.EfficiencyFlagsContainer';
import { ProductCardV2TireEfficiencyFlags } from '@packages/modules/src/ProductCardV2/ProductCardV2.TireEfficiencyFlags.client';
import { ProductCardV2PowerSupply } from '@packages/modules/src/ProductCardV2/ProductCardV2.PowerSupply';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';

import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinAdClick } from '@packages/tracking/src/types/events';
import { GlycerinPlacement } from '@packages/tracking/src/types/enums';

import { useHrefWithPageNumber } from '../../hooks/useHrefWithPageNumber';
import { encodePage } from '../../queryEncoding';
import { useMappedEfficiencyFlags2 } from '../../hooks/useMappedEfficiencyFlags';
import { mapSearchPowerSupplyData } from '../../utils/mapSearchPowerSupplyData';
import { getDeviceIndependentDisplayMode } from '../../utils/getDeviceIndependentDisplayMode';
import { ProductListPrice } from '../ProductList/Cards/ProductList.Price';
import { ProductListAvailabilityOverlay } from '../ProductList/Cards/ProductList.AvailabilityOverlay';

/* GraphQL */ `
  fragment CriteoProductCardResultFragment on SearchProductResult {
    id
    ...UseDisplayModeFragment
    abTesting {
      newTestAssignments {
        testId
      }
    }
  }
`;

/* GraphQL */ `
  fragment CriteoProductCardContentFragment on StylePreviewItemBase {
    styleId # needed?
    styleName
    categoryName # tracking for criteo the same as regular cards?
    brand {
      name
      image
    }
    efficiencyFlags {
      ...MapEfficiencyFlagsFragment
    }
    ratings {
      averageValue
      count
    }
    sellingPoints
    primaryVariationGroup {
      groupName
      groupDescription
      sku
      image
      href
      price {
        ...ProductListPriceFragment
      }
      availability {
        ...ProductListAvailabilityOverlayFragment
      }
      searchPowerSupply {
        ...SearchPowerSupplyFragment
      }
    }
  }
`;

const sponsoredFlagText = defineMessage({
  id: 'search.productList.sponsored.flag',
  defaultMessage: 'Gesponsert',
});

export const priceMessages = defineMessages({
  msrp: {
    id: 'price.label.msrp',
    defaultMessage: 'UVP',
  },
  oldPrice: {
    id: 'price.label.oldPrice',
    defaultMessage: 'statt',
  },
  startPricePrefix: { id: 'price.start.from', defaultMessage: 'ab' },
});

export type CriteoProductCardContentProps = {
  maskedResult: FragmentType<typeof CriteoProductCardResultFragmentFragmentDoc>;
  maskedItem: FragmentType<typeof CriteoProductCardContentFragmentFragmentDoc>;
  pageNumber: number;
  priority?: boolean;
  onClick?: () => void;
  hasReceivedFocus: boolean;
};

/**
 * A search-page-specific wrapper around `ProductCard`
 */
export const CriteoProductCardContent: FC<CriteoProductCardContentProps> = (props) => {
  const { formatMessage } = useIntl();
  const searchParams = useSearchParams();

  const { maskedItem, maskedResult, pageNumber, priority, onClick, hasReceivedFocus } = props;

  const result = unmask<typeof CriteoProductCardResultFragmentFragmentDoc>(maskedResult);
  const item = unmask<typeof CriteoProductCardContentFragmentFragmentDoc>(maskedItem);

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  const activeVariationGroup = item.primaryVariationGroup;

  const { displayMode } = getDeviceIndependentDisplayMode(result, searchParams);
  const { setRelevantProduct } = useAbTesting();

  // NOTE: for some reason this does not update with `history.replaceState` correctly, but the page is overwritten below anyways
  const url = useUrl(true);
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const dispatchGTMEvent = useTracking();

  const href = useHrefWithPageNumber(activeVariationGroup.href, pageNumber);

  const dispatchProductClickTracking = () => {
    dispatchGTMEvent<GTMEventGlycerinAdClick>({
      event: 'AdClick',
      AdClickData: {
        adId: 'criteo',
        adType: 'criteo',
        category: 'criteo-card-click',
        target: href,
        label: `${item.brand.name} ${item.styleName}`,
        placement: GlycerinPlacement.LISTING,
      },
    });
  };

  const handleClick = () => {
    dispatchProductClickTracking();

    const queryChange = encodePage(pageNumber);
    pushQueryChange(queryChange, { shallow: true });

    const modifiedUrl = mergeQueryParameters(url, queryChange);
    rememberPageData(item.styleId, modifiedUrl);

    // later steps in the customer flow need to be able to correlate the product with the test (e.g. in detail view or checkout)
    for (const testAssignment of result.abTesting?.newTestAssignments ?? []) {
      setRelevantProduct(testAssignment.testId, item.styleId);
    }

    onClick?.();
  };

  const name = activeVariationGroup.groupDescription ?? item.styleName;

  const { energyEfficiencyFlags, tireEfficiencyFlags } = useMappedEfficiencyFlags2(
    item.efficiencyFlags,
  );

  const powerSupplyProps = activeVariationGroup.searchPowerSupply && {
    ...mapSearchPowerSupplyData(activeVariationGroup.searchPowerSupply),
    asCard: false,
    showPowerSupplyMessage: false,
  };

  return (
    <>
      <ProductCardV2Name id={item.styleId} href={href} onClick={handleClick}>
        {name}
      </ProductCardV2Name>
      <ProductCardV2Brand id={item.styleId}>{item.brand.name}</ProductCardV2Brand>

      {displayMode === 'moreInformation' && (
        <ProductCardV2ExtendedDescription bulletPoints={item.sellingPoints} />
      )}

      {/* prevent rendering the overlay for every card, because it doesn't matter until the user hovers the card */}
      {hasReceivedFocus && activeVariationGroup.availability && (
        <ProductListAvailabilityOverlay
          href={href}
          groupName={activeVariationGroup.groupName ?? undefined}
          maskedAvailability={activeVariationGroup.availability}
        />
      )}

      <ProductListPrice styleId={item.styleId} maskedPrice={activeVariationGroup.price} />

      {item.ratings && (
        <ProductCardV2Rating
          id={item.styleId}
          avgRating={item.ratings.averageValue}
          reviewCount={item.ratings.count}
        />
      )}

      {((energyEfficiencyFlags?.length ?? 0) > 0 || (tireEfficiencyFlags?.length ?? 0) > 0) && (
        <ProductCardV2EfficiencyFlagsContainer>
          {energyEfficiencyFlags && energyEfficiencyFlags.length > 0 && (
            <ProductCardV2EnergyEfficiencyFlags flags={energyEfficiencyFlags} />
          )}
          {tireEfficiencyFlags && tireEfficiencyFlags.length > 0 && (
            <ProductCardV2TireEfficiencyFlags flags={tireEfficiencyFlags} />
          )}
        </ProductCardV2EfficiencyFlagsContainer>
      )}

      {powerSupplyProps && <ProductCardV2PowerSupply {...powerSupplyProps} />}

      {/* TODO SEARCH-2950 move everything above here to `ProductList.SharedCardContent.tsx` */}

      <ProductCardV2Image src={activeVariationGroup.image} alt="" priority={priority} />

      <ProductCardV2FlagsContainer>
        <Flag text={formatMessage(sponsoredFlagText)} variant="neutral" />
      </ProductCardV2FlagsContainer>
    </>
  );
};
